import { memo } from "react";
import type { MouseEvent } from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Tooltip from "@mui/material/Tooltip";
import type { Order } from "@/api/order/_models/Order";
import { toTitleCase } from "@/app/text.utils";
import { OverflowTooltip } from "@/components/OverflowTooltip";
import { OrderListExpanded } from "./order-list-expanded/OrderListExpanded";
import { OrderListTemplate } from "./OrderListTemplate";
import { OrderListStatusChip } from "./order-list-status-chip/OrderListStatusChip";
import { OrderListSystemChip } from "./order-list-system-chip/OrderListSystemChip";
import { TimeAgo } from "@/components/TimeAgo";
import { toOrderListSystemSummaryList } from "./toOrderListSystemSummaryList";

interface OrderListRowProps {
  isOpen: boolean;
  onSelect: (orderId: string) => void;
  onToggle: (orderId: string) => void;
  order: Order;
}

const OPEN_MARKER_PX = 3;

const OrderListRowContent = (props: OrderListRowProps) => {
  const { isOpen, onSelect, onToggle, order } = props;
  const orderNumber = order.orderNumber || "—";
  let customerName = "—";
  if (order.customerOrder?.customerName) customerName = toTitleCase(order.customerOrder.customerName);
  const statusCount = toOrderListSystemSummaryList(order).filter((summary) => summary.status).length;

  const handleToggle = () => {
    onToggle(order.id);
  };

  const handleSelect = (event: MouseEvent) => {
    event.stopPropagation();
    onSelect(order.id);
  };

  return (
    <Box
      sx={{
        bgcolor: isOpen ? "action.hover" : "transparent",
        borderBottom: 1,
        borderColor: "divider",
        boxShadow: (theme) => (isOpen ? `inset ${OPEN_MARKER_PX}px 0 0 ${(theme.vars ?? theme).palette.primary.main}` : "none"),
      }}
    >
      <OrderListTemplate
        onClick={handleToggle}
        sx={{
          cursor: "pointer",
          py: 1,
          "&:hover": { bgcolor: isOpen ? "transparent" : "action.hover" },
        }}
      >
        <IconButton
          aria-label={isOpen ? "Collapse order" : "Expand order"}
          size="small"
          sx={{ gridArea: "expand" }}
        >
          <KeyboardArrowDownIcon
            fontSize="small"
            sx={{
              transform: isOpen ? "rotate(180deg)" : "none",
              transition: "transform 160ms",
            }}
          />
        </IconButton>
        <OverflowTooltip title={orderNumber}>
          <Typography
            noWrap
            sx={{
              fontWeight: 600,
              gridArea: "orderNumber",
            }}
            variant="body2"
          >
            {orderNumber}
          </Typography>
        </OverflowTooltip>
        <OverflowTooltip title={customerName}>
          <Typography
            noWrap
            sx={{ gridArea: "customer" }}
            variant="body2"
          >
            {customerName}
          </Typography>
        </OverflowTooltip>
        <Box sx={{ gridArea: "source" }}>
          {order.source && <OrderListSystemChip system={order.source} />}
        </Box>
        <Box sx={{ gridArea: "status" }}>
          <OrderListStatusChip
            count={statusCount}
            status={order.status}
          />
        </Box>
        <TimeAgo
          dayOnly
          iso={order.orderDate}
          sx={{ gridArea: "orderDate" }}
        />
        <TimeAgo
          iso={order.lastSyncedAt}
          sx={{
            color: "text.secondary",
            gridArea: "lastSynced",
          }}
        />
        <Tooltip title="Open details">
          <IconButton
            aria-label="Open order details"
            onClick={handleSelect}
            size="small"
            sx={{ gridArea: "detail" }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </OrderListTemplate>
      <Collapse
        in={isOpen}
        unmountOnExit
      >
        <OrderListExpanded order={order} />
      </Collapse>
    </Box>
  );
};

/** One order in the list: clicking the row expands it inline; the far-right icon opens its details. Memoised. */
export const OrderListRow = memo(OrderListRowContent);
